import React, { useEffect, useState } from "react";
import { useNavigate, useRouteError } from "react-router-dom";
import { motion } from "framer-motion";

// Assuming primaryColor is imported from theme
const primaryColor = "#3487F3"; // Fallback if theme import fails

export default function ErrorPage() {
  const navigate = useNavigate();
  const error = useRouteError();
  const [countdown, setCountdown] = useState(5);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      navigate(-1); // Go back to previous page instead of home
    }, 5000);

    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          clearTimeout(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearTimeout(timer);
      clearInterval(countdownInterval);
    };
  }, [navigate]);

  const errorTitle = error?.status === 404 
    ? "Page not found" 
    : "Something went wrong";
  
  const errorMessage = error?.status === 404
    ? "The page you are looking for might have been removed, had its name changed, or is temporarily unavailable."
    : "We've encountered an unexpected error. Our team has been notified and is working to fix the issue.";

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-white dark:bg-gray-900 p-4">
      <div className="max-w-md w-full text-center space-y-8">
        <motion.div initial={{ opacity: 0, y: -50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          {error?.status === 404 ? (
            <h1 className="text-9xl font-extrabold" style={{ color: primaryColor }}>
              <motion.span
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                4
              </motion.span>
              <motion.span
                initial={{ rotateY: 90, opacity: 0 }}
                animate={{ rotateY: 0, opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="inline-block"
              >
                0
              </motion.span>
              <motion.span
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.5 }}
              >
                4
              </motion.span>
            </h1>
          ) : (
            <motion.div 
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="mx-auto w-24 h-24 flex items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-12 w-12" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke={primaryColor}
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            </motion.div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.5 }}
          className="space-y-6"
        >
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">Oops! {errorTitle}</h2>
          <p className="text-gray-600 dark:text-gray-400">
            {errorMessage}
          </p>
          
          {error && (
            <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
              <h3 className="text-lg font-semibold text-red-700 dark:text-red-400">Error Details</h3>
              <p className="text-sm text-red-600 dark:text-red-300 mt-1 break-words">
                {error.statusText || error.message || "An unexpected error occurred"}
              </p>
              {error.status && (
                <p className="text-xs text-red-500 dark:text-red-400 mt-1">Status: {error.status}</p>
              )}
              {error.stack && (
                <details className="mt-2">
                  <summary className="text-xs text-red-500 dark:text-red-400 cursor-pointer">Technical Details</summary>
                  <pre className="mt-2 text-xs text-left p-2 bg-red-50 dark:bg-red-900/30 rounded overflow-x-auto">
                    {error.stack}
                  </pre>
                </details>
              )}
            </div>
          )}

          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 1, duration: 0.5 }}
            className="relative w-32 h-32 mx-auto"
          >
            <motion.div
              animate={{rotate: 360}}
              transition={{
                duration: 20,
                repeat: Number.POSITIVE_INFINITY,
                ease: "linear"
              }}
              className="absolute inset-0"
            >
              {Array.from({ length: 12 }).map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-2 h-2 rounded-full"
                  style={{
                    backgroundColor: primaryColor,
                    top: "50%",
                    left: "50%",
                    transform: `rotate(${i * 30}deg) translateY(-40px) translateX(-50%)`
                  }}
                  initial={{ opacity: 0.3 }}
                  animate={{ opacity: [0.3, 1, 0.3] }}
                  transition={{
                    duration: 2,
                    repeat: Number.POSITIVE_INFINITY,
                    delay: i * 0.1
                  }}
                />
              ))}
            </motion.div>
            <div className="absolute inset-0 flex items-center justify-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: [0, 1.2, 1] }}
                transition={{ delay: 1.2, duration: 0.5 }}
                className="text-4xl font-bold"
                style={{ color: primaryColor }}
              >
                {countdown}
              </motion.div>
            </div>
          </motion.div>

          <p className="text-sm text-gray-500 dark:text-gray-400">
            Returning to previous page in {countdown} seconds...
          </p>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1.4, duration: 0.5 }}
            className="flex justify-center gap-3 relative z-50"
          >
            <button
              onClick={() => navigate('/', { replace: true })}
              className="flex items-center justify-center gap-2 px-6 py-2 rounded-md text-white font-medium transition-all hover:brightness-110 hover:shadow-lg cursor-pointer"
              style={{ backgroundColor: primaryColor }}
            >
             
              Go Home
            </button>
            <button
              onClick={() => navigate(-1, { replace: true })}
              className="flex items-center justify-center gap-2 px-6 py-2 rounded-md text-white font-medium transition-all hover:brightness-110 hover:shadow-lg cursor-pointer"
              style={{ backgroundColor: primaryColor }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                pointerEvents="none"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Go Back
            </button>
            
            <button
              onClick={() => window.location.reload()}
              className="flex items-center justify-center gap-2 px-6 py-2 rounded-md border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 font-medium transition-all hover:bg-gray-50 dark:hover:bg-gray-800 hover:shadow-lg"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                pointerEvents="none"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Try Again
            </button>
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        className="absolute bottom-0 w-full pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.5 }}
        style={{ zIndex: 10 }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" className="w-full">
          <path
            fill={primaryColor}
            fillOpacity="0.2"
            d="M0,224L48,213.3C96,203,192,181,288,181.3C384,181,480,203,576,224C672,245,768,267,864,250.7C960,235,1056,181,1152,165.3C1248,149,1344,171,1392,181.3L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
          ></path>
        </svg>
      </motion.div>
    </div>
  );
}